import { useEffect, useRef, useState } from "react";
import { db } from "../../firebase/config.js";
import Modal from "../common/Modal.jsx";
import Table from "../common/table/Table.jsx";
import TableCell from "../common/table/TableCell.jsx";
import usePersonaTerms from '../../hooks/usePersonaTerms';

const pad2 = (n) => String(n).padStart(2, "0");

const formatHHmm = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const REASON_LABEL = {
  vakantie: "vakantie",
  ziek: "ziek",
  verlof: "verlof",
  overig: "overig",
};

const SelectTeamMembers = ({
  taskDocid = "",
  teamMembers = [],
  selectedIds = [],
  onChange,
  memberRole,
  onSaved,
  availability = {},
}) => {
  // 'Medewerker' bij de FIXtool, 'Deelnemer' elders (verzoek BuurtKlusBedrijf 2026-09-28).
  const personaTerms = usePersonaTerms();
  const [localSelected, setLocalSelected] = useState([]);
  const [absentConfirm, setAbsentConfirm] = useState(null); // { memberId, reason }

  const saveTimerRef = useRef(null);

  // Sync local UI state with controlled value
  useEffect(() => {
    setLocalSelected(Array.isArray(selectedIds) ? selectedIds : []);
  }, [selectedIds]);

  const persistSelection = (nextSelected) => {
    if (typeof onChange === "function") onChange(nextSelected);

    if (!taskDocid) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      await db
        .collection("Tasks")
        .doc(taskDocid)
        .set({ TeamMembers: nextSelected }, { merge: true });

      if (typeof onSaved === "function") onSaved();
    }, 250);
  };

  const applyToggle = (memberId) => {
    setLocalSelected((prev) => {
      const next = prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId];

      persistSelection(next);
      return next;
    });
  };

  const handleCheckboxChange = (memberId) => {
    const isSelected = localSelected.includes(memberId);
    const a = availability?.[memberId];

    // Confirm only when ADDING an absent persona — removing is unrestricted.
    if (!isSelected && a?.status === "absent") {
      setAbsentConfirm({ memberId, reason: a.reason || "overig" });
      return;
    }

    applyToggle(memberId);
  };

  const confirmAbsentAdd = () => {
    if (!absentConfirm) return;
    applyToggle(absentConfirm.memberId);
    setAbsentConfirm(null);
  };

  const selectAllTeamMembers = () => {
    const next = teamMembers.map((m) => m.ID);
    setLocalSelected(next);
    persistSelection(next);
  };

  const clearAllTeamMembers = () => {
    const next = [];
    setLocalSelected(next);
    persistSelection(next);
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const renderAvailabilityBadge = (memberId) => {
    const a = availability?.[memberId];
    if (!a) return null;
    if (a.status === "absent") {
      const label = REASON_LABEL[a.reason] || a.reason || "afwezig";
      // De tijden staan er alleen bij als ze zijn ingevuld én de afwezigheid
      // binnen één dag valt; anders is dit precies zoals het was.
      return (
        <span className="select-team-member-absent-badge">
          Afwezig: {label}{a.times ? ` — ${a.times}` : ""}
        </span>
      );
    }
    if (a.status === "outside-hours") {
      return (
        <span className="select-team-member-outside-hours-badge">
          Buiten rooster
        </span>
      );
    }
    if (a.status === "busy" && a.conflict) {
      const { title, start, end } = a.conflict;
      const range = `${formatHHmm(start)}–${formatHHmm(end)}`;
      return (
        <span className="select-team-member-busy-badge">
          Bezet: {title || "andere taak"} — {range}
        </span>
      );
    }
    if (a.status === "free") {
      return (
        <span
          className="select-team-member-free-dot"
          title="Beschikbaar"
        />
      );
    }
    return null;
  };

  const absentMember = absentConfirm
    ? teamMembers.find((m) => m.ID === absentConfirm.memberId)
    : null;

  return (
    <div>
      <p>Kies één of meerdere teamleden/{personaTerms.pluralLower} om toe te voegen aan deze taak.</p>

      <div>
        <button
          type="button"
          className="select-team-members-selector-button"
          onClick={selectAllTeamMembers}
        >
          Alles selecteren
        </button>
        <button
          type="button"
          className="select-team-members-selector-button"
          onClick={clearAllTeamMembers}
        >
          Wissen
        </button>
      </div>

      <div className="select-team-member-for-task-list-container">
        <Table
          columns={[
            { key: "select", label: "" },
            { key: "name", label: "NAAM" },
            { key: "role", label: "ROL" },
            { key: "availability", label: "BESCHIKBAARHEID" },
          ]}
          items={teamMembers}
          showSearch={false}
          getItemKey={(member) => member.ID}
          emptyMessage={`Geen teamleden of ${personaTerms.pluralLower} gevonden.`}
          renderRow={(member) => (
            <>
              <TableCell>
                <input
                  type="checkbox"
                  checked={localSelected.includes(member.ID)}
                  onChange={() => handleCheckboxChange(member.ID)}
                />
              </TableCell>
              <TableCell>{member.Name}</TableCell>
              <TableCell>{memberRole(member)}</TableCell>
              <TableCell>{renderAvailabilityBadge(member.ID)}</TableCell>
            </>
          )}
        />
      </div>

      <Modal
        modalOpen={!!absentConfirm}
        setModalOpen={(open) => {
          if (!open) setAbsentConfirm(null);
        }}
        confirm={confirmAbsentAdd}
        pageHeader="Persona is afwezig"
        confirmLabel="Toch toevoegen"
        cancelLabel="Annuleren"
        content={
          <p>
            {absentMember?.Name || "Deze persona"} is afwezig
            {absentConfirm
              ? ` (${REASON_LABEL[absentConfirm.reason] || absentConfirm.reason})`
              : ""}
            {" "}op deze datum. Toch toevoegen aan de taak?
          </p>
        }
      />
    </div>
  );
};

export default SelectTeamMembers;
